import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import {
  LoadingState,
  Notice,
  OrganizationMark,
  ProductLockup,
  StatusView,
  traceButtonClass,
} from '@traceio-org/auth-ui';
import { isPlatformApiError } from '@traceio-org/platform-client';
import {
  PRODUCTS,
  STAFF_ROLE_LABELS,
  invitationPath,
  type InvitationPreview,
  type ProductKey,
} from '@traceio-org/platform-contracts';
import { useHub } from '../app/context';
import { DevSection, devNoticeText } from '../app/dev-identity';
import {
  isSessionEnded,
  loginRoute,
  readHubState,
  sessionExpiredPath,
  useFollowDecision,
  useLoad,
  usePageHeading,
} from '../app/hooks';
import { AccountActions, HubFrame, PageHeading } from '../app/ui';
import { safeHubPath } from '../security/hub-path';

type InvitationStatus = InvitationPreview['status'];

type AcceptState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'email-mismatch' }
  // The organization requires its own sign-in. `loop`: a sign-in for that very reason just happened.
  | { kind: 'sso-required'; loop: boolean; connectionHint?: string | undefined }
  | { kind: 'error' };

const EXPIRY_FORMAT = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short' });

function productOf(preview: InvitationPreview): ProductKey | null {
  return preview.kind === 'portal' ? 'portal' : (preview.productKey ?? null);
}

function invitationTitle(preview: InvitationPreview): string {
  if (preview.kind === 'staff') return 'Join Trace Admin';
  if (preview.kind === 'portal' && preview.portal) return `Join ${preview.portal.displayName}`;
  return `Join ${preview.organization.displayName}`;
}

function ClosedInvitation({ status, preview }: { status: Exclude<InvitationStatus, 'pending'>; preview: InvitationPreview }) {
  // A staff invitation comes from a Trace platform admin, not from an organization.
  const org = preview.kind === 'staff' ? 'a Trace platform admin' : preview.organization.displayName;
  const productKey = productOf(preview);
  const copy = {
    expired: { title: 'This invitation has expired', body: `Ask ${org} to send you a new invitation.` },
    revoked: {
      title: 'This invitation was withdrawn',
      body: `It is no longer valid. Ask ${org} for a new invitation if you still need access.`,
    },
    accepted: { title: 'This invitation was already accepted', body: 'Sign in to continue to your workspace.' },
  }[status];
  const headingRef = usePageHeading(copy.title);
  return (
    <StatusView
      tone={status === 'accepted' ? 'info' : 'warning'}
      title={copy.title}
      headingRef={headingRef}
      primaryAction={
        <Link className={traceButtonClass('primary', { block: true })} to={loginRoute(productKey)}>
          {status === 'accepted' ? 'Sign in' : 'Go to sign-in'}
        </Link>
      }
    >
      <p>{copy.body}</p>
    </StatusView>
  );
}

function InvitationDetails({ preview }: { preview: InvitationPreview }) {
  const { config } = useHub();
  const productKey = productOf(preview);
  return (
    <section className="trace-auth-invitation" aria-label="Invitation details">
      {/* A staff invitation has no organization: the frame already shows the Trace Admin lockup. */}
      {preview.kind === 'staff' ? null : (
        <OrganizationMark branding={preview.organization.branding} assetBaseUrl={config.platformApiUrl} />
      )}
      <dl className="trace-auth-details">
        <div>
          <dt>Product</dt>
          <dd>
            {preview.kind === 'portal' && preview.portal ? (
              `${preview.portal.displayName}, for ${preview.portal.customerAccountName}`
            ) : productKey ? (
              <ProductLockup productKey={productKey} />
            ) : (
              'Trace'
            )}
          </dd>
        </div>
        {preview.kind === 'staff' && preview.staffRole ? (
          <div>
            <dt>Role</dt>
            <dd>{STAFF_ROLE_LABELS[preview.staffRole]}</dd>
          </div>
        ) : null}
        <div>
          <dt>Invited email</dt>
          <dd>{preview.emailHint}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>
            <time dateTime={preview.expiresAt}>{EXPIRY_FORMAT.format(new Date(preview.expiresAt))}</time>
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * /invitations/:token: a public preview (organization, product, masked email, expiry). Signed out, the
 * person signs in first and comes back here (the invitation path rides in the sign-in state); signed in,
 * they accept and follow the returned decision. The token never appears in the page title.
 */
export function InvitationPage() {
  const { token = '' } = useParams();
  const deps = useHub();
  const navigate = useNavigate();
  const location = useLocation();
  const { reauthenticated } = useMemo(() => readHubState(location.state), [location.state]);
  const follow = useFollowDecision();
  const [preview, retryPreview] = useLoad((signal) => deps.client.invitationPreview(token, { signal }), [deps, token]);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [accept, setAccept] = useState<AcceptState>({ kind: 'idle' });
  const [closedStatus, setClosedStatus] = useState<Exclude<InvitationStatus, 'pending'> | null>(null);
  const [startError, setStartError] = useState(false);

  useEffect(() => {
    let active = true;
    deps.sessions.restore().then(
      (session) => {
        if (active) setSignedIn(session !== null);
      },
      () => {
        if (active) setSignedIn(false);
      },
    );
    return () => {
      active = false;
    };
  }, [deps]);

  const afterDevSignIn = useCallback(async () => {
    setSignedIn(true);
    return null;
  }, []);

  if (preview.status === 'loading') {
    return (
      <HubFrame productKey={null}>
        <PageHeading title="Invitation" />
        <LoadingState label="Loading the invitation" />
      </HubFrame>
    );
  }

  if (preview.status === 'error') {
    return (
      <HubFrame productKey={null}>
        <InvitationError error={preview.error} onRetry={retryPreview} />
      </HubFrame>
    );
  }

  const data = preview.data;
  const productKey = productOf(data);
  const status = closedStatus ?? data.status;

  if (status !== 'pending') {
    return (
      <HubFrame productKey={productKey}>
        <ClosedInvitation status={status} preview={data} />
        <InvitationDetails preview={data} />
      </HubFrame>
    );
  }

  async function acceptInvitation() {
    setAccept({ kind: 'busy' });
    try {
      const result = await deps.client.acceptInvitation(token);
      const plan = await follow(result.decision, {
        ...(productKey ? { productKey } : {}),
        contextId: result.contextId,
        mode: deps.sessions.active()?.mode ?? 'auth0',
      });
      if (plan.type === 'navigate') setAccept({ kind: 'idle' });
    } catch (error) {
      if (isSessionEnded(error)) {
        navigate(sessionExpiredPath(productKey), { replace: true });
        return;
      }
      const code = isPlatformApiError(error) ? error.code : undefined;
      if (code === 'sso_required') {
        await followSsoRequirement(isPlatformApiError(error) ? error.problem.hints : undefined);
        return;
      }
      if (code === 'invitation_expired') setClosedStatus('expired');
      else if (code === 'invitation_revoked') setClosedStatus('revoked');
      else if (code === 'invitation_already_used') setClosedStatus('accepted');
      setAccept(code === 'invitation_email_mismatch' ? { kind: 'email-mismatch' } : { kind: 'error' });
    }
  }

  /**
   * The organization enforces its own sign-in and this session came through another connection; nothing
   * was accepted. With Auth0, sign in again through the organization (the API's hints) and come back to
   * this page, once: right after such a sign-in the page explains instead of restarting again. Development
   * identity cannot switch connections, so it says which identity to use.
   */
  async function followSsoRequirement(hints: { organization?: string; connection?: string } | undefined) {
    const auth0 = deps.sessions.auth0;
    const development = deps.sessions.active()?.mode === 'development';
    if (!auth0 || development || reauthenticated === 'sso_required') {
      setAccept({ kind: 'sso-required', loop: !development && auth0 !== null, connectionHint: hints?.connection });
      return;
    }
    try {
      await auth0.beginSignIn({
        ...(productKey ? { productKey } : {}),
        hubPath: safeHubPath(invitationPath(token)),
        ...(hints?.organization ? { organization: hints.organization } : {}),
        ...(hints?.connection ? { connection: hints.connection } : {}),
        reauthenticate: 'sso_required',
      });
    } catch (startError) {
      console.warn('account-hub: could not start sign-in', startError);
      setAccept({ kind: 'error' });
    }
  }

  /**
   * The portal signs people in with email codes only, and its invitation is accepted only from that sign-in (the
   * API answers sso_required otherwise), so a portal invitation signs in through the portal's email connection
   * straight away instead of the employee methods. Without the login options the API's hint still gets there.
   */
  async function portalConnection(): Promise<string | undefined> {
    if (data.kind !== 'portal') return undefined;
    try {
      const options = await deps.client.loginOptions('portal');
      return options.methods.find((method) => method.type === 'passwordless-email')?.connection;
    } catch (error) {
      console.warn('account-hub: could not load the portal sign-in options', error);
      return undefined;
    }
  }

  async function signInToAccept() {
    const auth0 = deps.sessions.auth0;
    if (!auth0) return;
    setStartError(false);
    try {
      const connection = await portalConnection();
      await auth0.beginSignIn({
        ...(productKey ? { productKey } : {}),
        hubPath: safeHubPath(invitationPath(token)),
        ...(connection ? { connection } : {}),
      });
    } catch (error) {
      console.warn('account-hub: could not start sign-in', error);
      setStartError(true);
    }
  }

  const productLabel = data.kind === 'portal' ? 'the customer portal' : productKey ? PRODUCTS[productKey].displayName : 'Trace';
  return (
    <HubFrame productKey={productKey}>
      <PageHeading
        title={invitationTitle(data)}
        lead={
          data.kind === 'staff'
            ? 'You are invited to Trace Admin as Trace staff. Trace Admin asks for a second factor when you sign in.'
            : `You are invited to use ${productLabel} with ${data.organization.displayName}.`
        }
      />
      <InvitationDetails preview={data} />
      {signedIn === null ? <LoadingState label="Checking your session" /> : null}

      {signedIn === true ? (
        <div className="trace-auth-stack">
          {accept.kind === 'email-mismatch' ? (
            <Notice tone="error" live="assertive" title="This invitation is for a different email address.">
              <p>It was sent to {data.emailHint}. Sign in with that address to accept it.</p>
            </Notice>
          ) : null}
          {accept.kind === 'sso-required' && accept.loop ? (
            <Notice tone="warning" live="assertive" title="We could not confirm the sign-in this organization requires.">
              <p>Sign in again with the sign-in method of your organization, or use a different account.</p>
            </Notice>
          ) : null}
          {accept.kind === 'sso-required' && !accept.loop ? (
            <Notice
              tone="warning"
              live="assertive"
              title={devNoticeText(
                {
                  kind: 'reauthenticate',
                  reason: 'sso_required',
                  ...(accept.connectionHint ? { connectionHint: accept.connectionHint } : {}),
                },
                productKey,
              )}
            />
          ) : null}
          {accept.kind === 'error' ? (
            <Notice tone="error" live="assertive" title="We could not accept the invitation. Try again." />
          ) : null}
          <button
            type="button"
            className={traceButtonClass('primary', { block: true })}
            onClick={() => void acceptInvitation()}
            disabled={accept.kind === 'busy'}
            aria-busy={accept.kind === 'busy' || undefined}
          >
            Accept invitation
          </button>
          <AccountActions productKey={productKey} />
        </div>
      ) : null}

      {signedIn === false ? (
        <div className="trace-auth-stack">
          <p className="trace-auth-muted">Sign in with the email address the invitation was sent to.</p>
          {startError ? (
            <Notice tone="error" live="assertive" title="We could not reach the sign-in service. Try again." />
          ) : null}
          {deps.sessions.auth0 ? (
            <button type="button" className={traceButtonClass('primary', { block: true })} onClick={() => void signInToAccept()}>
              Sign in to accept
            </button>
          ) : (
            <Notice title="Sign-in is not configured in this environment." />
          )}
          <DevSection enabled probe afterSignIn={afterDevSignIn} />
        </div>
      ) : null}
    </HubFrame>
  );
}

/** Copy for a preview that failed; an API may also answer closed invitations with a problem code. */
function invitationErrorCopy(error: unknown): { title: string; body: string; retry: boolean } {
  const code = isPlatformApiError(error) ? error.code : undefined;
  if (code === 'invitation_expired') {
    return { title: 'This invitation has expired', body: 'Ask the person who invited you to send a new one.', retry: false };
  }
  if (code === 'invitation_revoked') {
    return { title: 'This invitation was withdrawn', body: 'Ask the person who invited you for a new one.', retry: false };
  }
  if (code === 'invitation_already_used') {
    return { title: 'This invitation was already accepted', body: 'Sign in to continue to your workspace.', retry: false };
  }
  if (isPlatformApiError(error) && error.status === 404) {
    return {
      title: 'We could not find this invitation',
      body: 'Check the link in your invitation email. Invitation links are long, so make sure you used all of it.',
      retry: false,
    };
  }
  return { title: 'We could not load this invitation', body: 'Check your connection, then try again.', retry: true };
}

function InvitationError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const copy = invitationErrorCopy(error);
  const headingRef = usePageHeading(copy.title);
  return (
    <StatusView
      tone={copy.retry ? 'error' : 'warning'}
      title={copy.title}
      headingRef={headingRef}
      primaryAction={
        copy.retry ? (
          { label: 'Try again', onClick: onRetry }
        ) : (
          <Link className={traceButtonClass('primary', { block: true })} to="/login">
            Go to sign-in
          </Link>
        )
      }
    >
      <p>{copy.body}</p>
    </StatusView>
  );
}
